import moment from "moment-timezone";


export const getEventTitle = (appointment) => {
  if (appointment.job?.report?.name) {
    return appointment.job.report.name;
  }
  if (appointment.job?.note) {
    return appointment.job.note;
  }
  if (appointment.note) {
    return appointment.note;
  }
  return "Untitled Appointment";
};

export const formatAddressObject = (addressObj) => {
  if (typeof addressObj === 'string') {
    return addressObj;
  }
  
  if (!addressObj || typeof addressObj !== 'object') {
    return '';
  }
  
  const parts = [];
  
  if (addressObj.address_line1) {
    parts.push(addressObj.address_line1);
  }
  
  if (addressObj.address_line2) {
    parts.push(addressObj.address_line2);
  }
  
  const cityStateZip = [];
  if (addressObj.city) {
    cityStateZip.push(addressObj.city);
  }
  if (addressObj.state) {
    cityStateZip.push(addressObj.state);
  }
  if (addressObj.zip_code) {
    cityStateZip.push(addressObj.zip_code);
  }
  
  if (cityStateZip.length > 0) {
    parts.push(cityStateZip.join(', '));
  }
  
  return parts.join(', ');
};

export const getEventAddress = (appointment) => {
  if (appointment.job?.report?.address) {
    return formatAddressObject(appointment.job.report.address);
  }
  if (appointment.job?.address) {
    return formatAddressObject(appointment.job.address);
  }
  return "";
};

// export const getEventStatus = (appointment) => {
//   // Map appointment status to calendar display status
//   if (appointment.status) {
//     return appointment.status;
//   }
  
//   // Infer status from other fields if not explicitly set
//   if (appointment.job?.report?.has_compiled_reports) {
//     return "completed";
//   }
//   if (appointment.job?.report?.item_count > 0) {
//     return "under-inspection";
//   }
  
//   return "booked";
// };

export const getEventColor = (status) => {
  const colors = {
    booked: {
      background: "#FFDA6A",
      border: "#FFC107",
      text: "#997404",
    },
    "pre-inspection": {
      background: "#6EDFF6",
      border: "#0DCAF0",
      text: "#087990",
    },
    "under-inspection": {
      background: "#80B2FF",
      border: "#075AD8",
      text: "#084298",
    },
    completed: {
      background: "#DEE2E6",
      border: "#6C757D",
      text: "#495057",
    },
  };
  
  // Handle case-insensitive matching and API format variations
  const normalizedStatus = status?.toLowerCase();
  
  // Map common API variations to our internal format
  const statusMap = {
    "booked": "booked",
    "pre-inspection": "pre-inspection",
    "preinspection": "pre-inspection",
    "under-inspection": "under-inspection",
    "underinspection": "under-inspection",
    "under inspection": "under-inspection",
    "completed": "completed",
  };
  
  const mappedStatus = statusMap[normalizedStatus] || normalizedStatus;
  return colors[mappedStatus] || colors.booked;
};

export const filterEventsByDateRange = (events, startDate, endDate) => {
  const start = moment(startDate).startOf("day");
  const end = moment(endDate).endOf("day");
  
  return events.filter((event) => {
    const eventStart = moment(event.start);
    const eventEnd = moment(event.end);
    
    // Check if event overlaps with date range
    return (
      (eventStart.isSameOrAfter(start) && eventStart.isSameOrBefore(end)) ||
      (eventEnd.isSameOrAfter(start) && eventEnd.isSameOrBefore(end)) ||
      (eventStart.isBefore(start) && eventEnd.isAfter(end))
    );
  });
};

export const filterEventsByStatus = (events, status) => {
  if (!status || status === "all") {
    return events;
  }
  
  return events.filter((event) => event.tag === status || event.status === status);
};

export const sortEventsByStartTime = (events) => {
  return [...events].sort((a, b) => 
    moment(a.start).valueOf() - moment(b.start).valueOf()
  );
};

export const groupEventsByDate = (events) => {
  const grouped = {};
  
  events.forEach((event) => {
    const dateKey = moment(event.start).format("YYYY-MM-DD");
    if (!grouped[dateKey]) {
      grouped[dateKey] = [];
    }
    grouped[dateKey].push(event);
  });
  
  // Sort events within each day
  Object.keys(grouped).forEach((dateKey) => {
    grouped[dateKey] = sortEventsByStartTime(grouped[dateKey]);
  });
  
  return grouped;
};

export const hasEventConflict = (newEvent, existingEvents) => {
  const newStart = moment(newEvent.start);
  const newEnd = moment(newEvent.end);
  
  return existingEvents.some((event) => {
    const existingStart = moment(event.start);
    const existingEnd = moment(event.end);
    
    // Check for overlap
    return (
      (newStart.isSameOrAfter(existingStart) && newStart.isBefore(existingEnd)) ||
      (newEnd.isAfter(existingStart) && newEnd.isSameOrBefore(existingEnd)) ||
      (newStart.isSameOrBefore(existingStart) && newEnd.isSameOrAfter(existingEnd))
    );
  });
};

export const getEventDurationText = (event) => {
  const duration = moment(event.end).diff(moment(event.start), "minutes");
  
  if (duration < 60) {
    return `${duration} min`;
  }
  
  const hours = Math.floor(duration / 60);
  const minutes = duration % 60;
  
  if (minutes === 0) {
    return `${hours}h`;
  }
  
  return `${hours}h ${minutes}m`;
};

export const getEventDisplayText = (event, view = "month") => {
  const startTime = moment(event.start).format("h:mm A");
  
  if (view === "month") {
    // Month view: show time and abbreviated info
    return `${startTime} ${event.job?.report?.id || event.job?.report?.name || "Appointment"}`;
  }
  
  if (view === "week") {
    // Week view: show more details
    const lines = [];
    if (event.job?.report?.id) {
      lines.push(event.job.report.id);
    }
    if (event.job?.report?.address) {
      lines.push(formatAddressObject(event.job.report.address));
    }
    return lines.join("\n");
  }
  
  return event.title;
};

export const isEventMultiDay = (event) => {
  const start = moment(event.start);
  const end = moment(event.end);
  return !start.isSame(end, "day");
};

export const getEventsByMonth = (events, date) => {
  const startOfMonth = moment(date).startOf("month");
  const endOfMonth = moment(date).endOf("month");
  
  return events.filter((event) => {
    const eventStart = moment(event.start);
    return eventStart.isBetween(startOfMonth, endOfMonth, "day", "[]");
  });
};

export const getEventsByWeek = (events, date) => {
  const startOfWeek = moment(date).startOf("week");
  const endOfWeek = moment(date).endOf("week");
  
  return events.filter((event) => {
    const eventStart = moment(event.start);
    return eventStart.isBetween(startOfWeek, endOfWeek, "day", "[]");
  });
};

export const getEventsByDay = (events, date) => {
  const targetDay = moment(date).startOf("day");
  
  return events.filter((event) => {
    const eventStart = moment(event.start);
    return eventStart.isSame(targetDay, "day");
  });
};